import {
  Notification,
  NotificationChangedRealtimePayload,
  NotificationApiResponse,
  NotificationListContent,
  NotificationRealtimePayload,
} from '@/types/notification';
import { NotificationTypeValue } from '@/modules/notifications/components/notification-icon';

const realtimeNotificationCodeByValueMap: Partial<Record<NotificationTypeValue, string>> = {
  [NotificationTypeValue.PaymentSuccess]: 'PaymentSuccess',
  [NotificationTypeValue.DocumentsResubmitted]: 'DocumentsResubmitted',
  [NotificationTypeValue.DocumentsSubmitted]: 'DocumentsSubmitted',
};

export const allowedHeaderRealtimeNotificationValues = new Set<number>([
  NotificationTypeValue.PaymentSuccess,
  NotificationTypeValue.DocumentsResubmitted,
  NotificationTypeValue.DocumentsSubmitted,
]);

const normalizeNotificationType = (type: NotificationRealtimePayload['type']) => {
  if (typeof type === 'string') return type;
  return type?.code ?? '';
};

export const normalizeNotificationChangedPayload = (
  payload: NotificationChangedRealtimePayload,
): Notification | null => {
  const resolvedType = payload.Type ?? payload.type ?? null;
  const typeValue = resolvedType?.value;
  const id = payload.Id ?? payload.id;
  const createdAtUtc = payload.CreatedAtUtc ?? payload.createdAtUtc;

  if (!id || !createdAtUtc) {
    return null;
  }

  if (!typeValue || !allowedHeaderRealtimeNotificationValues.has(typeValue)) {
    return null;
  }

  return {
    id,
    orderId: payload.OrderId ?? payload.orderId ?? null,
    orderItemId: payload.OrderItemId ?? payload.orderItemId ?? null,
    type: resolvedType?.code ?? realtimeNotificationCodeByValueMap[typeValue as NotificationTypeValue] ?? '',
    title: payload.Title ?? payload.title ?? '',
    body: payload.Body ?? payload.body ?? '',
    isRead: payload.IsRead ?? payload.isRead ?? false,
    readAtUtc: payload.ReadAtUtc ?? payload.readAtUtc ?? null,
    emailSent: payload.EmailSent ?? payload.emailSent ?? false,
    emailSentAtUtc: payload.EmailSentAtUtc ?? payload.emailSentAtUtc ?? null,
    createdAtUtc,
  };
};

export const normalizeNotification = (
  notification: NotificationRealtimePayload | Notification,
): Notification => {
  return {
    id: notification.id,
    orderId: notification.orderId ?? null,
    orderItemId: notification.orderItemId ?? null,
    type: normalizeNotificationType(notification.type),
    title: notification.title,
    body: notification.body,
    isRead: notification.isRead,
    readAtUtc: notification.readAtUtc ?? null,
    emailSent: notification.emailSent,
    emailSentAtUtc: notification.emailSentAtUtc ?? null,
    createdAtUtc: notification.createdAtUtc,
  };
};

export const buildNotificationListContent = (content: any): NotificationListContent => {
  const rawNotifications = content?.notifications ?? content?.items ?? content ?? [];
  const notifications = Array.isArray(rawNotifications)
    ? rawNotifications.map((item) => normalizeNotification(item))
    : [];

  const unreadCount =
    content?.unreadCount ??
    content?.statistics?.unreadCount ??
    notifications.filter((item) => !item.isRead).length;
  const totalCount = content?.totalCount ?? content?.totalElements ?? notifications.length;
  const hasMore = content?.hasMore ?? content?.page < content?.totalPages;

  return {
    notifications,
    unreadCount,
    totalCount,
    hasMore: !!hasMore,
  };
};

export const toNotificationApiResponse = <TContent>(data: any, fallbackContent: TContent): NotificationApiResponse<TContent> => {
  return {
    content: (data?.content ?? fallbackContent) as TContent,
    succeeded: !!data?.succeeded,
    messages: Array.isArray(data?.messages) ? data.messages : [],
  };
};

export const formatNotificationRelativeTime = (timestamp: Date) => {
  const diffMs = Date.now() - timestamp.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Vừa xong';
  if (diffMinutes < 60) return `${diffMinutes} phút trước`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} giờ trước`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} ngày trước`;
};
